import type { IContentItem } from "@typing/wsui/referenceContent";

/**
 * 检测字符串是否为Markdown格式
 * @param text 需要检测的字符串
 * @returns 如果包含Markdown语法特征返回true，否则返回false
 */
export const isMarkdownFormat = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    
    const trimmedText = text.trim();
    
    // Markdown特征检测正则表达式
    const markdownPatterns = [
        // 标题 (#, ##, ### 等)
        /^#{1,6}\s+\S+/m,
        // 无序列表 (*, -, +)
        /^(\s*)[*+-]\s+\S+/m,
        // 有序列表 (1., 2., 3. 等)
        /^(\s*)\d+\.\s+\S+/m,
        // 代码块 (``` 或 四个空格)
        /^```[\s\S]*?```|^ {4,}\S+/m,
        // 链接 ([文字](链接))
        /\[[^\]]+\]\([^)]+\)/,
        // 图片 (![alt](src))
        /!\[[^\]]*\]\([^)]+\)/,
        // 粗体 (**文字** 或 __文字__)
        /\*\*[^*]+\*\*|__[^_]+__/,
        // 斜体 (*文字* 或 _文字_)
        /\*[^*]+\*|_[^_]+_/,
        // 引用 (> 文字)
        /^>\s+\S+/m,
        // 水平分割线 (---, ***)
        /^[-*]{3,}$/m,
        // 表格 (| 表头 |)
        /^\|[\s\S]*\|$/m,
        // 数学公式 ($公式$ 或 $公式$)
        /\$[^$]+\$|\$\$[^$]+\$\$/,
        // 说明书中常见的内联 HTML 标签，如 WS<sub>3</sub>
        /<\/?(?:sub|sup)>/i
    ];
    
    // 检查是否包含任何Markdown特征
    return markdownPatterns.some(pattern => pattern.test(trimmedText));
};

/**
 * 检测数据格式是否为markdown格式（整个页面内容）
 * @param data 接口返回的数据
 * @returns 如果是markdown格式返回true，否则返回false
 */
export const isMarkdownFormatData = (data: { content_list: IContentItem[] }): boolean => {
    return data.content_list.length === 1 && data.content_list[0].key === 'markdown';
};

// 目录项数据类型
export interface TocItem {
    id: string;
    label: string;
    level: number; // 层级：1-一级，2-二级，3-三级
    children?: TocItem[];
}

/**
 * 从markdown内容中解析目录结构
 * @param markdownContent markdown格式的内容
 * @returns 目录项列表
 */
export const parseMarkdownToc = (markdownContent: string): TocItem[] => {
    const tocItems: TocItem[] = [];
    const lines = markdownContent.split('\n');
    
    // 解析标题：只识别#, ##, ###
    const rawHeadings: { level: number; title: string; lineIndex: number }[] = [];
    
    lines.forEach((line, index) => {
        // 只匹配#, ##, ###标题
        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            const level = headingMatch[1].length; // #的数量表示级别
            const title = headingMatch[2].trim();
            if (title) {
                rawHeadings.push({
                    level,
                    title,
                    lineIndex: index
                });
            }
        }
    });
    
    if (rawHeadings.length === 0) {
        return tocItems;
    }
    
    // 构建三级目录结构
    let currentLevel1Index = -1;
    let currentLevel2Index = -1;
    
    rawHeadings.forEach((heading, index) => {
        if (heading.level === 1) {
            // # 一级标题
            const id = `section-${tocItems.length}`;
            tocItems.push({
                id,
                label: heading.title,
                level: 1,
                children: []
            });
            currentLevel1Index = tocItems.length - 1;
            currentLevel2Index = -1;
        } else if (heading.level === 2) {
            // ## 二级标题
            // 如果有父级一级标题，创建子项；否则创建新的一级标题
            if (currentLevel1Index >= 0) {
                const parent = tocItems[currentLevel1Index];
                const id = `subsection-${parent.children!.length}`;
                parent.children!.push({
                    id,
                    label: heading.title,
                    level: 2,
                    children: []
                });
                currentLevel2Index = parent.children!.length - 1;
            } else {
                // 没有一级标题，直接创建一级标题
                const id = `section-${tocItems.length}`;
                tocItems.push({
                    id,
                    label: heading.title,
                    level: 1,
                    children: []
                });
                currentLevel1Index = tocItems.length - 1;
                currentLevel2Index = -1;
            }
        } else if (heading.level === 3) {
            // ### 三级标题
            if (currentLevel1Index >= 0) {
                if (currentLevel2Index >= 0) {
                    // 有父级二级标题，创建三级子项
                    const level1Parent = tocItems[currentLevel1Index];
                    const level2Parent = level1Parent.children![currentLevel2Index];
                    const id = `subsection-${currentLevel2Index}-${level2Parent.children!.length}`;
                    level2Parent.children!.push({
                        id,
                        label: heading.title,
                        level: 3,
                        children: []
                    });
                } else {
                    // 有父级一级标题但无二级标题，创建二级子项
                    const parent = tocItems[currentLevel1Index];
                    const id = `subsection-${parent.children!.length}`;
                    parent.children!.push({
                        id,
                        label: heading.title,
                        level: 2,
                        children: []
                    });
                    currentLevel2Index = parent.children!.length - 1;
                }
            } else {
                // 没有父级标题，直接创建一级标题
                const id = `section-${tocItems.length}`;
                tocItems.push({
                    id,
                    label: heading.title,
                    level: 1,
                    children: []
                });
                currentLevel1Index = tocItems.length - 1;
                currentLevel2Index = -1;
            }
        }
    });
    
    return tocItems;
};

/**
 * 统一目录生成逻辑，支持两种数据格式
 * @param contentList 接口返回的内容列表
 * @param markdownContent 可选的markdown内容
 * @returns 目录结构
 */
export const generateTocFromContent = (contentList: IContentItem[], level: number = 1): TocItem[] => {
    // 原有的树形结构目录生成逻辑
    if (!contentList || contentList.length === 0) return [];
    
    return contentList.filter(item => (item.content || item.children))?.map((item) => ({
        id: item.key,
        label: item.key_desc,
        level,
        children: item.children ? generateTocFromContent(item.children, level + 1) : undefined,
    }));
};

/**
 * 为markdown内容添加锚点ID
 * @param content markdown内容
 * @param tocData 目录数据
 * @returns 添加了锚点ID的内容
 */
export const processMarkdownWithAnchors = (content: string, tocData: TocItem[]): string => {
    // 递归查找标题对应的TocItem
    const findTocItem = (title: string, items: TocItem[]): TocItem | null => {
        for (const item of items) {
            if (item.label === title) {
                return item;
            }
            if (item.children) {
                const found = findTocItem(title, item.children);
                if (found) return found;
            }
        }
        return null;
    };
    
    return content.split('\n').map((line, index) => {
        // 匹配所有标题级别 (#, ##, ###)
        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
        
        if (headingMatch) {
            const title = headingMatch[2].trim();
            const headingLevel = headingMatch[1];
            const tocItem = findTocItem(title, tocData);
            
            if (tocItem) {
                // 使用不显示的锚点元素，确保准确的滚动定位
                return `${headingLevel} ${title}\n<a id="${tocItem.id}" style="display: block; visibility: hidden; height: 0;"></a>`;
            }
        }
        
        return line;
    }).join('\n');
}